/**
 * Styles WPConsent's consent banner to match the site's own design language
 * (see inc/consent-geo.php's doc comment on why this exists as JS, not CSS):
 * the banner renders inside a real open-mode Shadow DOM
 * (`#wpconsent-container`, attached in the plugin's own frontend.js), which
 * external stylesheets cannot select into at all -- only CSS custom
 * properties inherit across that boundary, and WPConsent's own `:host{}`
 * rule already hardcodes values for every one it exposes (background,
 * text, button colors -- those are set through the plugin's own Style tab
 * settings instead, see wp_option `wpconsent_settings`). Everything below
 * (entrance animation, shadow, radius, border, compact width, close-button
 * hover) has no settings-UI equivalent, so this injects a real <style>
 * element directly INTO the shadow root itself, where ordinary selectors
 * work normally again.
 *
 * Design intent (chosen after a design-options review, see
 * PROJECT_DOCS/decisions/2026-08-30-consent-banner-redesign.md): a
 * FULL-WIDTH BOTTOM BAR -- flush against the bottom edge, no backdrop,
 * doesn't cover page content. Uses WPConsent Lite's free "long" layout as
 * its structural base (set via wp_option `wpconsent_settings.banner_layout`,
 * `banner_position: bottom`) with only the accent border, entrance
 * transition, and left-text/right-buttons row alignment overridden below --
 * `long`'s own CSS already gives a full-width bottom-pinned bar with a
 * vertical button stack under 767px, which this keeps as-is.
 */

const STYLE_ELEMENT_ID = 'jctc-consent-banner-style';

const CSS = `
	#wpconsent-banner-holder {
		display: block !important;
		opacity: 0;
		visibility: hidden;
		pointer-events: none;
		overflow-x: hidden;
		max-width: 100vw;
		transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.5s cubic-bezier(0.16, 1, 0.3, 1);
	}

	#wpconsent-banner-holder.wpconsent-banner-visible {
		opacity: 1;
		visibility: visible;
		pointer-events: auto;
	}

	#wpconsent-banner-holder .wpconsent-banner {
		border-top: 1px solid #A49F6F; /* $c-bronze */
		justify-content: space-between;
		gap: 24px;
		padding: 18px 32px;
		box-sizing: border-box;
		max-width: 100%;
		width: 100%;
		left: 0;
		right: 0;
		transform: translateY(100%);
		transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
	}

	#wpconsent-banner-holder.wpconsent-banner-visible .wpconsent-banner {
		transform: translateY(0);
	}

	#wpconsent-banner-holder .wpconsent-banner-body {
		text-align: left;
	}

	@media (max-width: 767px) {
		#wpconsent-banner-holder .wpconsent-banner {
			padding: 16px 20px;
		}

		#wpconsent-banner-holder .wpconsent-banner-body {
			text-align: center;
		}
	}

	.wpconsent-banner-close {
		transition: color 0.3s ease, transform 0.3s ease;
	}

	.wpconsent-banner-close:hover,
	.wpconsent-banner-close:focus-visible {
		transform: scale(1.1);
	}

	/* Same convention as the theme's global safety net (base/_base.scss) --
	   0.01ms rather than 0s so transitionend still fires, !important so it
	   wins regardless of source order. That global rule can't reach in here
	   (Shadow DOM boundary), so it's repeated locally. */
	@media (prefers-reduced-motion: reduce) {
		#wpconsent-banner-holder,
		#wpconsent-banner-holder .wpconsent-banner,
		.wpconsent-banner-close {
			transition-duration: 0.01ms !important;
		}
	}
`;

/**
 * Keeps `styleEl` as the shadow root's LAST child. WPConsent's own
 * frontend.js loads its frontend.css asynchronously and, on a load failure
 * or 5s timeout, appends its own hardcoded fallback <style> -- if that
 * lands after ours (same selectors, same specificity), source order lets
 * IT win and silently reverts the banner to WPConsent's generic look
 * (plain 10px radius, gray shadow) for the rest of the page view. Observing
 * and re-appending is idempotent -- once nothing else has been added after
 * ours, the observer callback finds it already last and does nothing, so
 * this can't loop.
 */
function keepStyleLast(shadowRoot, styleEl) {
    const observer = new MutationObserver(() => {
        if (shadowRoot.contains(styleEl) && shadowRoot.lastElementChild !== styleEl) {
            shadowRoot.appendChild(styleEl);
        }
    });
    observer.observe(shadowRoot, { childList: true });
}

function injectInto(shadowRoot) {
    if (!shadowRoot || shadowRoot.getElementById(STYLE_ELEMENT_ID)) {
        return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = CSS;
    shadowRoot.appendChild(style);
    keepStyleLast(shadowRoot, style);
}

function tryInject() {
    const container = document.getElementById('wpconsent-container');
    if (container?.shadowRoot) {
        injectInto(container.shadowRoot);
        return true;
    }
    return false;
}

if (!tryInject()) {
    // The shadow root doesn't exist yet -- WPConsent dispatches this event
    // (unconditionally, whether or not the banner actually ends up showing)
    // once its own init() has attached the shadow root and finished its
    // display-decision logic.
    window.addEventListener('wpconsent_banner_initialized', tryInject, { once: true });
}
